import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { DeviceService } from '../device/device.service';
import { DiskService } from '../disk/disk.service';
import { DB_KEY_PREFIXES, DB_SYNC_STATE_KEYS } from '../../config/pouch-db.config';
import { MarketScanDataService } from '../../data-services/market-scan/market-scan.data.service';
import { EventsService } from '../events/events.service';

/**
 * Published (no payload) whenever getRawRows() starts reflecting a new fetch/disk-load.
 * This sync runs as a fire-and-forget background task kicked off at the end of the main sync
 * (see MasterDataService/_syncDataInternal's post-promisePool call to
 * MarketScanService.startLadderBackgroundSync) that can take 2+ minutes — consumers that read
 * getRawRows() eagerly can run before that completes and see an empty/stale array. Subscribe to
 * this event to re-run once real data is in.
 */
export const ADOPTION_LADDER_DATA_UPDATED_EVENT = 'adoptionLadderData:updated';

/**
 * Raw (ungrouped) indskr_productrating row carrying adoption ladder only (segmentation and
 * adoption ladder live on mutually exclusive rows of the same entity — see
 * MarketScanDataService.buildProductRatingFetchXml). Position-scoped to the current user's
 * territory. indskr_customersegmentationid/indskr_name are always null here — kept in the shape
 * so callers can freely concatenate this service's rows with CustomerSegmentationOfflineService's
 * without type gymnastics.
 */
export interface ProductRatingRawRow {
  indskr_productratingid: string;
  rowStatecode: number;
  indskr_customersegmentationid: string | null;
  indskr_name: string | null;
  productId: string;
  contactId: string;
  adoptionLadderId: string;
}

@Injectable({
  providedIn: 'root'
})
export class ProductRatingOfflineService {
  private rawRows: ProductRatingRawRow[] = [];

  /** Consumers that would otherwise show "no data" (adoption-ladder chip, AI adoption-ladder
   * card, etc.) should check this before treating an empty getRawRows() as final — the
   * underlying fetch can take 2+ minutes and runs in the background (see MarketScanService.
   * startLadderBackgroundSync), so "empty" often just means "not synced yet". */
  public readonly syncState$ = new BehaviorSubject<'idle' | 'syncing' | 'ready' | 'error'>('idle');

  constructor(
    private readonly deviceService: DeviceService,
    private readonly diskService: DiskService,
    private readonly marketScanDataService: MarketScanDataService,
    private readonly events: EventsService,
  ) {}

  /** Last-known merged raw-row set, synchronously. Empty until the first sync/loadFromDB call. */
  public getRawRows(): ProductRatingRawRow[] {
    return this.rawRows;
  }

  public async loadFromDB(): Promise<ProductRatingRawRow[]> {
    await this.diskService.retrieve(DB_KEY_PREFIXES.PRODUCT_RATING_LADDER_RAW_ROWS).then((data) => {
      if (data && data['raw']) {
        this.rawRows = data['raw'];
      }
    }).catch((err) => console.error('[adoption_ladder_sync] loadFromDB: Error loading raw rows from offline db!', err));
    console.log(`[adoption_ladder_sync] offline-restore: ${this.rawRows.length} rows loaded from disk`);
    console.log(`[adoption_ladder_sync] publishing ${ADOPTION_LADDER_DATA_UPDATED_EVENT} (source=loadFromDB, rows=${this.rawRows.length})`);
    this.syncState$.next('ready');
    this.events.publish(ADOPTION_LADDER_DATA_UPDATED_EVENT);
    return this.rawRows;
  }

  /** Fetches + delta-merges indskr_productrating (adoption-ladder-only) raw rows, persists them,
   * and returns the full merged set. Callers own their own in-flight/background-sync guarding —
   * see MarketScanService.startLadderBackgroundSync for the existing pattern, since this fetch
   * alone can take 2+ minutes. */
  public async sync(forceFullSync = false): Promise<ProductRatingRawRow[]> {
    if (this.deviceService.isOffline) {
      console.log('[adoption_ladder_sync] device offline — falling back to loadFromDB() instead of a live fetch');
      return this.loadFromDB();
    }

    this.syncState$.next('syncing');
    try {
      // Confirmed via live testing: getSyncState() returns raw null when no prior state doc exists —
      // it does NOT auto-create one. Must construct and upsert a fresh doc ourselves the first time.
      const syncState = (await this.diskService.getSyncState(DB_SYNC_STATE_KEYS.SYNC_PRODUCT_RATING_LADDER))
        ?? { _id: DB_SYNC_STATE_KEYS.SYNC_PRODUCT_RATING_LADDER };
      const isInitialSync = forceFullSync || !syncState.lastUpdatedTime;
      // Captured before the fetch starts (not derived from server data) so we don't miss anything
      // modified during the fetch's own round-trip.
      const newLastUpdatedTime = new Date().getTime().toString();

      const rows = await this.marketScanDataService.getAdoptionLadderRatingsRaw(
        isInitialSync ? undefined : syncState.lastUpdatedTime);

      if (isInitialSync) {
        this.rawRows = rows;
      } else {
        this.mergeRawRows(rows);
      }

      // upsert=true (needs _id, drops any _rev) when this doc doesn't exist yet on disk;
      // upsert=false (needs an existing _rev) when we're updating a doc getSyncState found.
      const isNewSyncStateDoc = !syncState._rev;
      syncState.lastUpdatedTime = newLastUpdatedTime;
      await this.diskService.updateSyncState(syncState, isNewSyncStateDoc);
      await this.diskService.updateOrInsert(DB_KEY_PREFIXES.PRODUCT_RATING_LADDER_RAW_ROWS, () => ({ raw: this.rawRows }))
        .catch((err) => console.error('ProductRatingOfflineService.sync: Error saving raw rows to offline db!', err));

      console.log(`[adoption_ladder_sync] ${isInitialSync ? 'full' : 'delta'} sync: ${rows.length} rows fetched, ${this.rawRows.length} total rows cached`);
      console.log(`[adoption_ladder_sync] publishing ${ADOPTION_LADDER_DATA_UPDATED_EVENT} (source=sync, rows=${this.rawRows.length})`);
      this.syncState$.next('ready');
      this.events.publish(ADOPTION_LADDER_DATA_UPDATED_EVENT);
      return this.rawRows;
    } catch (error) {
      console.error('[adoption_ladder_sync] sync failed', error);
      this.syncState$.next('error');
      throw error;
    }
  }

  /**
   * Reflects a ladder stage change made in the Journey tab in the cached rows immediately, so
   * views derived from them (Spotlight's per-stage customer counts, contact.productSegmentations)
   * don't wait for the next delta sync. Does for a single row what sync() does for all of them:
   * patch, persist, announce. Pass null for adoptionLadderId to clear the stage.
   *
   * Safe against the next delta sync: the counts are derived by walking these rows rather than
   * accumulated, and mergeRawRows replaces by indskr_productratingid — so the server's row simply
   * overwrites this optimistic one, correcting it if the remote write did not take effect.
   */
  public async applyLocalLadderStageChange(productRatingId: string, adoptionLadderId: string | null): Promise<void> {
    if (!productRatingId) return;
    const row = this.rawRows.find(r => r.indskr_productratingid === productRatingId);
    // The ladder fetch filters on indskr_adoptionladder not-null, so a rating that has never
    // carried a stage has no cached row to patch — nothing to keep in step until the next sync.
    if (!row || row.adoptionLadderId === adoptionLadderId) return;
    row.adoptionLadderId = adoptionLadderId;
    await this.diskService.updateOrInsert(DB_KEY_PREFIXES.PRODUCT_RATING_LADDER_RAW_ROWS, () => ({ raw: this.rawRows }))
      .catch((err) => console.error('ProductRatingOfflineService.applyLocalLadderStageChange: Error saving raw rows to offline db!', err));
    this.events.publish(ADOPTION_LADDER_DATA_UPDATED_EVENT);
  }

  /** Upserts (active) or removes (inactive) delta rows into the maintained raw-row cache, keyed by
   * indskr_productratingid. Mutates rawRows in place. */
  private mergeRawRows(deltaRows: ProductRatingRawRow[]): void {
    const rowsById = new Map(this.rawRows.map(row => [row.indskr_productratingid, row]));
    for (const row of deltaRows) {
      if (row.rowStatecode === 0) {
        rowsById.set(row.indskr_productratingid, row);
      } else {
        rowsById.delete(row.indskr_productratingid);
      }
    }
    this.rawRows = Array.from(rowsById.values());
  }
}
